/**
 * `lists` procedure group — `contact_lists` + `contact_list_items`.
 *
 * A list is org-owned. Sharing a list fans out into `contact_access`, which is why
 * `shareCount` is carried on the row: a list with no shares skips the fan-out entirely.
 */

import { z } from 'zod';
import {
  cursorSchema,
  isoDateTimeSchema,
  jsonObjectSchema,
  nonBlankString,
  paginationInputSchema,
  uuidSchema,
} from './common.ts';
import { contactSummarySchema } from './contacts.ts';
import {
  accessSourceFilterSchema,
  accessSourceSchema,
  listKindSchema,
  shareScopeSchema,
  visibilitySchema,
} from './enums.ts';

/** Max contacts accepted by one `lists.items.add` / `lists.items.remove` call. */
export const MAX_LIST_ITEM_BATCH = 500;

export const listSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  kind: listKindSchema,
  /** Present only for `kind: 'smart'`; opaque to clients other than the query builder. */
  smartQuery: jsonObjectSchema.nullable(),
  ownerUserId: uuidSchema.nullable(),
  visibility: visibilitySchema,
  itemCount: z.int().min(0),
  shareCount: z.int().min(0),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  deletedAt: isoDateTimeSchema.nullable(),
});

/** How the caller reaches a list. Mirrors `contactAccessSchema`'s shape. */
export const listAccessSchema = z.object({
  source: accessSourceSchema,
  scope: shareScopeSchema.nullable(),
  canEdit: z.boolean(),
  canAdopt: z.boolean(),
  ownerOrgId: uuidSchema,
  ownerOrgName: z.string().nullable(),
  shareId: uuidSchema.nullable(),
});

export const listItemSchema = z.object({
  listId: uuidSchema,
  contactId: uuidSchema,
  position: z.int().nullable(),
  note: z.string().nullable(),
  addedBy: uuidSchema.nullable(),
  addedAt: isoDateTimeSchema,
});

/**
 * `lists.get` returns the list plus its first page of members — the detail screen
 * always needs both, and a second round trip on open is a visible stall.
 */
export const listDetailSchema = listSchema.extend({
  access: listAccessSchema,
  members: z.array(contactSummarySchema),
  membersNextCursor: cursorSchema.nullable(),
});

// --- inputs ------------------------------------------------------------------

export const listListInputSchema = paginationInputSchema.extend({
  q: z.string().min(1).max(200).optional(),
  kind: listKindSchema.optional(),
  source: accessSourceFilterSchema.default('all'),
  includeDeleted: z.boolean().default(false),
});

export const listGetInputSchema = paginationInputSchema.extend({
  id: uuidSchema,
});

export const listCreateInputSchema = z.object({
  name: nonBlankString(200, 'name'),
  description: z.string().max(2_000).nullish(),
  kind: listKindSchema.default('static'),
  smartQuery: jsonObjectSchema.nullish(),
  visibility: visibilitySchema.default('org'),
  /** Seed membership; the same cap as `lists.items.add`. */
  contactIds: z.array(uuidSchema).max(MAX_LIST_ITEM_BATCH).default([]),
});

export const listUpdateInputSchema = z.object({
  id: uuidSchema,
  name: nonBlankString(200, 'name').optional(),
  description: z.string().max(2_000).nullish(),
  smartQuery: jsonObjectSchema.nullish(),
  visibility: visibilitySchema.optional(),
});

export const listDeleteInputSchema = z.object({
  id: uuidSchema,
});

export const listDeleteOutputSchema = z.object({
  ok: z.literal(true),
  id: uuidSchema,
  deletedAt: isoDateTimeSchema,
});

export const listItemsAddInputSchema = z.object({
  listId: uuidSchema,
  contactIds: z.array(uuidSchema).min(1).max(MAX_LIST_ITEM_BATCH),
  note: z.string().max(500).nullish(),
});

export const listItemsRemoveInputSchema = z.object({
  listId: uuidSchema,
  contactIds: z.array(uuidSchema).min(1).max(MAX_LIST_ITEM_BATCH),
});

/** `skipped` counts contacts already present (add) or already absent (remove). */
export const listItemsMutationOutputSchema = z.object({
  ok: z.literal(true),
  listId: uuidSchema,
  changed: z.int().min(0),
  skipped: z.int().min(0),
  itemCount: z.int().min(0),
});

// --- inferred types ----------------------------------------------------------

export type ContactList = z.output<typeof listSchema>;
export type ContactListDetail = z.output<typeof listDetailSchema>;
export type ContactListItem = z.output<typeof listItemSchema>;
export type ListAccess = z.output<typeof listAccessSchema>;
export type ListCreateInput = z.input<typeof listCreateInputSchema>;
export type ListUpdateInput = z.input<typeof listUpdateInputSchema>;
